import { Grid } from "@material-ui/core";
import moment from "moment";
import { FunctionComponent, useCallback } from "react";
import styled from "styled-components";
import { Checkbox } from "../../components/forms/checkbox";
import { KycIcon } from "../../components/icons/status";
import { WrapGridCell } from "../../components/layout";
import { Resource } from "../../components/loader/resource";
import { ProcessingStatus } from "../../components/processing-status";
import { Widget } from "../../components/widget";
import { countries } from "../../store/common/models";
import { useParticipation } from "../../store/participations/queries";
import { ParticipationsDTO } from "../../store/participations/models/dto";

const StyledWidget = styled(Widget)`
  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 0;
`;

const Label = styled.span`
  font-weight: 500;
`;

export const InnerParticipationDetail: FunctionComponent<{
  item: ParticipationsDTO.Responses.GetSingle.T;
}> = ({ item }) => {
  const {
    user: {
      emailAddress,
      firstName,
      middleName,
      lastName,
      nationality,
      applicationPin,
    },
    referrer,
    createdDate,
    kycStatus,
    processingStatus,
    policies: {
      sekuritanceStatements,
      finalDeclaration,
      declarationStatements,
      whitepaperRead,
    },
    regulations,
    pledge,
    sourceWallet,
    destinationWallet,
    stakingWallet,
  } = item;
  return (
    <>
      <StyledWidget title="Participation Overview">
        <Grid container spacing={3}>
          <Grid item lg={6} xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Label>Name:</Label>
              </Grid>
              <Grid item xs={6}>
                {firstName} {middleName} {lastName}
              </Grid>
              <Grid item xs={6}>
                <Label>Email:</Label>
              </Grid>
              <Grid item xs={6}>
                {emailAddress}
              </Grid>
              <Grid item xs={6}>
                <Label>Nationality:</Label>
              </Grid>
              <Grid item xs={6}>
                {countries.enum[nationality as typeof countries.T]}
              </Grid>

              <Grid item xs={6}>
                <Label>Pledged Amount:</Label>
              </Grid>
              <Grid item xs={6}>
                {pledge?.amount}
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={6} xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Label>KYC Status:</Label>
              </Grid>
              <Grid item xs={6}>
                {KycIcon(kycStatus, "kyc")}
              </Grid>
              <Grid item xs={6}>
                <Label>Processing Status:</Label>
              </Grid>
              <Grid item xs={6}>
                <ProcessingStatus processingStatus={processingStatus} />
              </Grid>
              <Grid item xs={6}>
                <Label>Date Submitted:</Label>
              </Grid>
              <Grid item xs={6}>
                {moment.utc(createdDate).local().format("yyyy-MM-DD HH:mm")}
              </Grid>
              <Grid item xs={6}>
                <Label>Referrer:</Label>
              </Grid>
              <Grid item xs={6}>
                {referrer}
              </Grid>
              <Grid item xs={6}>
                <Label>Application Pin:</Label>
              </Grid>
              <Grid item xs={6}>
                {applicationPin}
              </Grid>
            </Grid>
          </Grid>
          {sourceWallet ? (
            <>
              <Grid item xs={3}>
                <Label>Source Wallet:</Label>
              </Grid>
              <WrapGridCell xs={9}>
                {sourceWallet.chain} {sourceWallet.address}
              </WrapGridCell>
            </>
          ) : null}
          {destinationWallet ? (
            <>
              <Grid item xs={3}>
                <Label>Destination Wallet:</Label>
              </Grid>
              <WrapGridCell xs={9}>
                {destinationWallet.chain} {destinationWallet.address}
              </WrapGridCell>
            </>
          ) : null}
          {stakingWallet ? (
            <>
              <Grid item xs={3}>
                <Label>Rewards Wallet:</Label>
              </Grid>
              <WrapGridCell xs={9}>
                {stakingWallet.chain} {stakingWallet.address}
              </WrapGridCell>
            </>
          ) : null}
        </Grid>
      </StyledWidget>
      <StyledWidget title="Participant Details">
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Label>Final Declaration</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={finalDeclaration} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>Pep</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={regulations?.pep} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>US Person</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={regulations?.usPerson} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>Reside Sanctioned Country</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox
              checked={regulations?.resideSanctionedCountry}
              disabled
            />
          </Grid>
          <Grid item xs={3}>
            <Label>Declaration Statements</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={declarationStatements} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>Whitepaper Read</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={whitepaperRead} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>Sekuritance Statements</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={sekuritanceStatements} disabled />
          </Grid>
        </Grid>
      </StyledWidget>
    </>
  );
};

export const ParticipationDetail: FunctionComponent<{ id: string }> = ({
  id,
}) => (
  <Resource r={useParticipation(id)}>
    {useCallback(
      (data: ParticipationsDTO.Responses.GetSingle.T) => (
        <InnerParticipationDetail item={data} />
      ),
      []
    )}
  </Resource>
);
